/* Cache bust: 2026-02-05T19:45:00 - Animations update */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography, IconButton, TextField } from '@mui/material';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import BrainSVG from './BrainSVG';

const getPromptCardBackground = (theme, isSelected) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  if (isWallpaperMode) {
    return isSelected ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)';
  }

  // For dark/light modes, keep the same background whether selected or not
  return theme.palette.mode === 'dark' ? '#1C1C1E' : '#F5F6FA';
};

const getPromptCardHoverBackground = (theme, isSelected) => {
  if (theme.palette.actualMode === 'wallpaper') {
    return 'rgba(255, 255, 255, 0.22)';
  }

  // Subtle hover effect for both selected and unselected states
  return theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(3, 133, 255, 0.04)';
};

const getPromptCardBorder = (theme, isSelected) => {
  // Show blue border when selected for all modes
  return isSelected
    ? `2px solid ${theme.palette.colors.ui.accent}`
    : '2px solid transparent';
};

const AppOnboarding = ({
  onComplete,
  onSkip,
  isSubmitting,
  onBirthdayChange,
  onGoalChange,
  onGoalDetailChange,
  onStepChange
}) => {
  const { t } = useTranslation();

  const steps = useMemo(() => ([
    {
      id: 'birthday',
      title: t('onboarding.birthday.title'),
      description: t('onboarding.birthday.description')
    },
    {
      id: 'goal',
      title: t('onboarding.goal.title'),
      description: ''
    },
    {
      id: 'goalDetail',
      title: '',
      description: ''
    },
    {
      id: 'presentLifeOS',
      title: t('onboarding.presentLifeOS.title'),
      description: ''
    },
    {
      id: 'presentAGT',
      title: t('onboarding.presentAGT.title'),
      description: ''
    }
  ]), [t]);

  const [introActive, setIntroActive] = useState(true);
  const [stepIndex, setStepIndex] = useState(0);
  const [welcomePhase, setWelcomePhase] = useState(-1);
  const [svgOffset, setSvgOffset] = useState(280);

  // Birthday state
  const currentDate = new Date();
  const [birthDay, setBirthDay] = useState(15);
  const [birthMonth, setBirthMonth] = useState(6); // June
  const [birthYear, setBirthYear] = useState(1990);

  // Goal state
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedFocusAreas, setSelectedFocusAreas] = useState([]);
  const [selectedExploration, setSelectedExploration] = useState(null);
  const [selectedOther, setSelectedOther] = useState(null);
  const [otherCustomText, setOtherCustomText] = useState('');

  const welcomeTextRef = useRef(null);
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  // Get translated month names
  const getMonthNames = () => [
    t('onboarding.birthday.months.january'),
    t('onboarding.birthday.months.february'),
    t('onboarding.birthday.months.march'),
    t('onboarding.birthday.months.april'),
    t('onboarding.birthday.months.may'),
    t('onboarding.birthday.months.june'),
    t('onboarding.birthday.months.july'),
    t('onboarding.birthday.months.august'),
    t('onboarding.birthday.months.september'),
    t('onboarding.birthday.months.october'),
    t('onboarding.birthday.months.november'),
    t('onboarding.birthday.months.december')
  ];

  // Get maximum days in a month for a given year
  const getMaxDaysInMonth = (month, year) => {
    // Month is 1-indexed (1 = January, 12 = December)
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    let maxDays = daysInMonth[month - 1];

    // Check for leap year in February
    if (month === 2) {
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      if (isLeapYear) {
        maxDays = 29;
      }
    }

    return maxDays;
  };

  // Validate and adjust day when month or year changes
  useEffect(() => {
    const maxDays = getMaxDaysInMonth(birthMonth, birthYear);
    if (birthDay > maxDays) {
      setBirthDay(maxDays);
    }
  }, [birthMonth, birthYear, birthDay]);

  // Auto-increment/decrement handler for holding down buttons
  const handleHoldStart = (incrementFunction) => {
    // Execute immediately on first press
    incrementFunction();

    // Start slower increment after a delay
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        incrementFunction();
      }, 100); // Change value every 100ms while holding
    }, 300); // Wait 300ms before starting auto-repeat
  };

  const handleHoldEnd = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleHoldEnd();
    };
  }, []);

  useEffect(() => {
    const calculateOffset = () => {
      if (welcomeTextRef.current) {
        const textWidth = welcomeTextRef.current.offsetWidth;
        // Gap responsive: 16px en móviles, 40px en tablets, 60px en desktop
        const gap = window.innerWidth < 600 ? 16 : window.innerWidth < 960 ? 35 : 55;
        setSvgOffset(textWidth / 2 + gap);
      }
    };

    calculateOffset();
    window.addEventListener('resize', calculateOffset);

    return () => {
      window.removeEventListener('resize', calculateOffset);
    };
  }, [welcomePhase]);

  useEffect(() => {
    if (!introActive) {
      setWelcomePhase(0);
      return undefined;
    }

    const showTimer = setTimeout(() => {
      setWelcomePhase(0);
    }, 150);

    const firstTimer = setTimeout(() => {
      setWelcomePhase(1);
    }, 3500);

    const secondTimer = setTimeout(() => {
      setWelcomePhase(2);
    }, 4500);

    const thirdTimer = setTimeout(() => {
      setWelcomePhase(3);
    }, 5300);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(firstTimer);
      clearTimeout(secondTimer);
      clearTimeout(thirdTimer);
    };
  }, [stepIndex, steps]);

  useEffect(() => {
    if (welcomePhase === 4) {
      const timer = setTimeout(() => {
        setIntroActive(false);
      }, 1200);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [welcomePhase]);

  const totalSteps = steps.length;
  const isLastStep = stepIndex === totalSteps - 1;
  const goalDetailMode = useMemo(() => {
    if (selectedGoals.includes('other')) return 'other';
    if (selectedGoals.includes('exploration')) return 'exploration';
    return 'area';
  }, [selectedGoals]);

  const isGoalDetailComplete = useMemo(() => {
    if (goalDetailMode === 'exploration') return Boolean(selectedExploration);
    if (goalDetailMode === 'other') {
      if (!selectedOther) return false;
      if (selectedOther === 'self') return otherCustomText.trim().length > 0;
      return true;
    }
    return selectedFocusAreas.length > 0;
  }, [goalDetailMode, selectedExploration, selectedOther, otherCustomText, selectedFocusAreas]);

  useEffect(() => {
    setSelectedFocusAreas([]);
    setSelectedExploration(null);
    setSelectedOther(null);
    setOtherCustomText('');
  }, [goalDetailMode]);

  const handleNext = () => {
    // Send data when leaving each data-collection step
    if (currentStep.id === 'birthday' && onBirthdayChange) {
      const birthday = new Date(birthYear, birthMonth - 1, birthDay);
      onBirthdayChange(birthday);
    }
    if (currentStep.id === 'goal' && onGoalChange && selectedGoals.length > 0) {
      onGoalChange(selectedGoals);
    }
    if (currentStep.id === 'goalDetail' && onGoalDetailChange) {
      onGoalDetailChange({
        mode: goalDetailMode,
        focusAreas: selectedFocusAreas,
        exploration: selectedExploration,
        other: selectedOther,
        otherText: otherCustomText.trim()
      });
    }

    if (isLastStep) {
      onComplete();
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, totalSteps - 1));
  };

  const handleBack = () => {
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const currentStep = steps[stepIndex];

  // Track step changes
  useEffect(() => {
    if (!introActive && onStepChange) {
      onStepChange({
        stepIndex,
        stepId: currentStep.id,
        totalSteps
      });
    }
  }, [stepIndex, introActive, currentStep.id, totalSteps, onStepChange]);

  if (introActive) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: { xs: 2, sm: 3, md: 4 },
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            minHeight: { xs: 140, sm: 180, md: 200 },
            position: 'relative',
            paddingX: { xs: 2, sm: 4 },
            mt: { xs: -6, sm: -5.5, md: -6.5 }
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 2 },
              opacity: welcomePhase === 0 ? 1 : 0,
              transform: welcomePhase === 0 ? 'translateY(0)' : 'translateY(-12px)',
              transition: 'opacity 0.6s ease, transform 0.6s ease',
              pointerEvents: welcomePhase === 0 ? 'auto' : 'none'
            }}
          >
            <Typography
              ref={welcomeTextRef}
              variant="h3"
              sx={{
                fontWeight: 700,
                textAlign: 'left',
                fontSize: { xs: '1.5rem', sm: '2.7rem', md: '3.2rem' },
                lineHeight: 1.1,
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                transform: 'translateX(-30px)',
                color: 'text.primary'
              }}
            >
              {t('onboarding.welcome')}
            </Typography>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: welcomePhase <= 1
                ? `translate(calc(-50% + ${svgOffset}px), -50%)`
                : welcomePhase === 2
                  ? 'translate(calc(-50% - 15px), -50%)'
                  : 'translate(-50%, calc(-50% - 80px))',
              transition: 'transform 1.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 1s ease',
              opacity: welcomePhase <= 3 ? 1 : 0,
              zIndex: 10
            }}
          >
            <Box
              sx={{
                animation: welcomePhase >= 3 && welcomePhase < 4 ? 'spin-brain 30s linear infinite' : 'none',
                animationDelay: welcomePhase >= 3 && welcomePhase < 4 ? '1.2s' : '0s',
                '@keyframes spin-brain': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              <BrainSVG size="clamp(70px, 12vw, 110px)" />
            </Box>
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              position: 'fixed',
              left: '50%',
              top: '50%',
              transform: welcomePhase >= 3 && welcomePhase < 4
                ? 'translate(-50%, calc(-50% + 30px))'
                : 'translate(-50%, calc(-50% + 42px))',
              opacity: welcomePhase >= 3 && welcomePhase < 4 ? 1 : 0,
              transition: 'opacity 1s ease, transform 0.6s ease',
              fontSize: { xs: '0.95rem', sm: '1.45rem', md: '1.6rem' },
              paddingX: { xs: 1, sm: 4 },
              textAlign: 'center',
              maxWidth: { xs: '96%', sm: '90%' },
              lineHeight: { xs: 1.4, sm: 1.4 },
              color: 'text.primary'
            }}
          >
            {t('onboarding.customize')}
          </Typography>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: 24,
            right: 24,
            opacity: welcomePhase >= 3 ? 1 : 0,
            transform: welcomePhase >= 3 ? 'translateY(0)' : 'translateY(-6px)',
            transition: 'opacity 0.4s ease 0.7s, transform 0.4s ease 0.7s'
          }}
        >
          <Button
            variant="text"
            onClick={() => onSkip({ stepId: 'intro', stepIndex: -1 })}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              textDecoration: 'underline',
              borderRadius: 999,
              color: (theme) =>
                theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.colors.system.grey,
              '&:hover': {
                backgroundColor: (theme) =>
                  theme.palette.actualMode === 'wallpaper' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)'
              }
            }}
          >
            {t('onboarding.skip')}
          </Button>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: 80, sm: 100, md: 120 },
            left: '50%',
            transform: welcomePhase >= 3 && welcomePhase < 4
              ? 'translate(-50%, 0)'
              : 'translate(-50%, 6px)',
            display: 'flex',
            justifyContent: 'center',
            opacity: welcomePhase >= 3 && welcomePhase < 4 ? 1 : 0,
            transition: 'opacity 1s ease, transform 0.4s ease'
          }}
        >
          <Button
            variant="contained"
            onClick={() => setWelcomePhase(4)}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              boxShadow: 'none',
              borderRadius: 999,
              backgroundColor: (theme) =>
                theme.palette.actualMode === 'wallpaper' ? 'rgba(255,255,255,0.18)' : theme.palette.colors.ui.accent,
              color: (theme) =>
                theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.colors.system.white,
              border: (theme) =>
                theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
              '&:hover': {
                backgroundColor: (theme) =>
                  theme.palette.actualMode === 'wallpaper' ? 'rgba(255,255,255,0.26)' : theme.palette.colors.ui.accent,
                opacity: theme => (theme.palette.actualMode === 'wallpaper' ? 1 : 0.9),
                boxShadow: 'none'
              }
            }}
          >
            {t('onboarding.continue')}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      key={`step-${stepIndex}`}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: { xs: 2, sm: 3, md: 4 },
        paddingTop: { xs: 10, sm: 12, md: 14 },
        paddingBottom: { xs: 20, sm: 22, md: 24 },
        position: 'relative',
        overflow: 'auto',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(20px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)'
          }
        },
        '@keyframes fadeInDown': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-20px)'
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)'
          }
        }
      }}
    >
      {/* Skip Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 24,
          right: 24
        }}
      >
        <Button
          variant="text"
          onClick={() => onSkip({ stepId: currentStep.id, stepIndex })}
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            textDecoration: 'underline',
            borderRadius: 999,
            color: (theme) =>
              theme.palette.actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : theme.palette.colors.system.grey,
            '&:hover': {
              backgroundColor: (theme) =>
                theme.palette.actualMode === 'wallpaper' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.06)',
              boxShadow: 'none'
            }
          }}
        >
          Skip
        </Button>
      </Box>

      {/* Birthday Content */}
      {currentStep.id === 'birthday' && (() => {
        const maxDays = getMaxDaysInMonth(birthMonth, birthYear);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                mb: 6,
                fontWeight: 600,
                textAlign: 'center',
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                color: 'text.primary',
                animation: 'fadeInDown 0.6s ease forwards'
              }}
            >
              {t('onboarding.birthday.title')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: { xs: 1.5, sm: 2 },
                justifyContent: 'center',
                mb: 4
              }}
            >
              {/* Day Selector */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  opacity: 0,
                  animation: 'fadeInUp 0.5s ease 0.15s forwards'
                }}
              >
                <Typography variant="caption" sx={{ mb: 1, opacity: 0.6, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 500, color: 'text.primary' }}>
                  {t('onboarding.birthday.day')}
                </Typography>
                <Box
                  sx={{
                    width: { xs: 70, sm: 80 },
                    height: { xs: 120, sm: 140 },
                    backgroundColor: (theme) =>
                      theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.15)'
                        : theme.palette.colors.ui.base,
                    backdropFilter: 'blur(10px)',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 1
                  }}
                >
                  <IconButton
                    size="small"
                    onMouseDown={() => handleHoldStart(() => setBirthDay(prev => prev === maxDays ? 1 : prev + 1))}
                    onMouseUp={handleHoldEnd}
                    onMouseLeave={handleHoldEnd}
                    onTouchStart={() => handleHoldStart(() => setBirthDay(prev => prev === maxDays ? 1 : prev + 1))}
                    onTouchEnd={handleHoldEnd}
                    sx={{
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                    }}
                  >
                    <ChevronUp size={20} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.6rem', sm: '2rem' },
                      fontWeight: 500,
                      lineHeight: 1,
                      userSelect: 'none',
                      color: 'text.primary'
                    }}
                  >
                    {birthDay}
                  </Typography>
                  <IconButton
                    size="small"
                    onMouseDown={() => handleHoldStart(() => setBirthDay(prev => prev === 1 ? maxDays : prev - 1))}
                    onMouseUp={handleHoldEnd}
                    onMouseLeave={handleHoldEnd}
                    onTouchStart={() => handleHoldStart(() => setBirthDay(prev => prev === 1 ? maxDays : prev - 1))}
                    onTouchEnd={handleHoldEnd}
                    sx={{
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                    }}
                  >
                    <ChevronDown size={20} />
                  </IconButton>
                </Box>
              </Box>

            {/* Month Selector */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: 0,
                animation: 'fadeInUp 0.5s ease 0.25s forwards'
              }}
            >
              <Typography variant="caption" sx={{ mb: 1, opacity: 0.6, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 500, color: 'text.primary' }}>
                {t('onboarding.birthday.month')}
              </Typography>
              <Box
                sx={{
                  width: { xs: 110, sm: 140 },
                  height: { xs: 120, sm: 140 },
                  backgroundColor: (theme) =>
                    theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.15)'
                      : theme.palette.colors.ui.base,
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 1
                }}
              >
                <IconButton
                  size="small"
                  onMouseDown={() => handleHoldStart(() => setBirthMonth(prev => prev === 12 ? 1 : prev + 1))}
                  onMouseUp={handleHoldEnd}
                  onMouseLeave={handleHoldEnd}
                  onTouchStart={() => handleHoldStart(() => setBirthMonth(prev => prev === 12 ? 1 : prev + 1))}
                  onTouchEnd={handleHoldEnd}
                  sx={{
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                  }}
                >
                  <ChevronUp size={20} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    fontWeight: 500,
                    lineHeight: 1,
                    textAlign: 'center',
                    userSelect: 'none',
                    color: 'text.primary'
                  }}
                >
                  {getMonthNames()[birthMonth - 1]}
                </Typography>
                <IconButton
                  size="small"
                  onMouseDown={() => handleHoldStart(() => setBirthMonth(prev => prev === 1 ? 12 : prev - 1))}
                  onMouseUp={handleHoldEnd}
                  onMouseLeave={handleHoldEnd}
                  onTouchStart={() => handleHoldStart(() => setBirthMonth(prev => prev === 1 ? 12 : prev - 1))}
                  onTouchEnd={handleHoldEnd}
                  sx={{
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                  }}
                >
                  <ChevronDown size={20} />
                </IconButton>
              </Box>
            </Box>

            {/* Year Selector */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                opacity: 0,
                animation: 'fadeInUp 0.5s ease 0.35s forwards'
              }}
            >
              <Typography variant="caption" sx={{ mb: 1, opacity: 0.6, fontSize: { xs: '0.7rem', sm: '0.75rem' }, fontWeight: 500, color: 'text.primary' }}>
                {t('onboarding.birthday.year')}
              </Typography>
              <Box
                sx={{
                  width: { xs: 85, sm: 100 },
                  height: { xs: 120, sm: 140 },
                  backgroundColor: (theme) =>
                    theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.15)'
                      : theme.palette.colors.ui.base,
                  backdropFilter: 'blur(10px)',
                  borderRadius: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: 1
                }}
              >
                <IconButton
                  size="small"
                  onMouseDown={() => handleHoldStart(() => setBirthYear(prev => Math.min(prev + 1, currentDate.getFullYear())))}
                  onMouseUp={handleHoldEnd}
                  onMouseLeave={handleHoldEnd}
                  onTouchStart={() => handleHoldStart(() => setBirthYear(prev => Math.min(prev + 1, currentDate.getFullYear())))}
                  onTouchEnd={handleHoldEnd}
                  sx={{
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                  }}
                >
                  <ChevronUp size={20} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: { xs: '1.4rem', sm: '1.75rem' },
                    fontWeight: 500,
                    lineHeight: 1,
                    userSelect: 'none',
                    color: 'text.primary'
                  }}
                >
                  {birthYear}
                </Typography>
                <IconButton
                  size="small"
                  onMouseDown={() => handleHoldStart(() => setBirthYear(prev => Math.max(prev - 1, currentDate.getFullYear() - 100)))}
                  onMouseUp={handleHoldEnd}
                  onMouseLeave={handleHoldEnd}
                  onTouchStart={() => handleHoldStart(() => setBirthYear(prev => Math.max(prev - 1, currentDate.getFullYear() - 100)))}
                  onTouchEnd={handleHoldEnd}
                  sx={{
                    color: 'text.primary',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                  }}
                >
                  <ChevronDown size={20} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Typography
            variant="body2"
            sx={{
              opacity: 0,
              textAlign: 'center',
              maxWidth: 400,
              fontSize: '0.875rem',
              color: 'text.primary',
              animation: 'fadeInUp 0.5s ease 0.5s forwards',
              '&': {
                animationFillMode: 'forwards'
              }
            }}
          >
            {t('onboarding.birthday.description')}
          </Typography>
        </Box>
        );
      })()}

      {/* Goal Selection */}
      {currentStep.id === 'goal' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 900, width: '100%', px: 2 }}>
          <Typography
            variant="h3"
            sx={{
              mb: 6,
              fontWeight: 600,
              textAlign: 'center',
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
              color: 'text.primary',
              animation: 'fadeInDown 0.6s ease forwards'
            }}
          >
            {t('onboarding.goal.title')}
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2.5,
            width: '100%',
            opacity: 0,
            animation: 'fadeInUp 0.5s ease 0.2s forwards'
          }}>
            {[
              {
                id: 'decisions',
                title: t('onboarding.goal.decisions.title'),
                description: t('onboarding.goal.decisions.description')
              },
              {
                id: 'problems',
                title: t('onboarding.goal.problems.title'),
                description: t('onboarding.goal.problems.description')
              },
              {
                id: 'speed',
                title: t('onboarding.goal.speed.title'),
                description: t('onboarding.goal.speed.description')
              },
              {
                id: 'resilience',
                title: t('onboarding.goal.resilience.title'),
                description: t('onboarding.goal.resilience.description')
              },
              {
                id: 'position',
                title: t('onboarding.goal.position.title'),
                description: t('onboarding.goal.position.description')
              },
              {
                id: 'exploration',
                title: t('onboarding.goal.exploration.title'),
                description: t('onboarding.goal.exploration.description')
              },
              {
                id: 'other',
                title: t('onboarding.goal.other.title'),
                description: t('onboarding.goal.other.description')
              }
            ].map((option) => {
              const isSelected = selectedGoals.includes(option.id);
              return (
                <Box
                  key={option.id}
                  onClick={() => {
                    setSelectedGoals((prev) => {
                      const isOther = option.id === 'other';
                      const isCurrentlySelected = prev.includes(option.id);

                      if (isOther) {
                        return isCurrentlySelected ? [] : ['other'];
                      }

                      const next = prev.filter((id) => id !== 'other');
                      if (isCurrentlySelected) {
                        return next.filter((id) => id !== option.id);
                      }
                      return [...next, option.id];
                    });
                  }}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: (theme) =>
                      getPromptCardBackground(theme, isSelected),
                    backdropFilter: (theme) =>
                      theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                    WebkitBackdropFilter: (theme) =>
                      theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                    borderRadius: 3,
                    border: (theme) => getPromptCardBorder(theme, isSelected),
                    padding: { xs: 2, sm: 2.5, md: 3 },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: (theme) =>
                        getPromptCardHoverBackground(theme, isSelected),
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: option.description ? 0.5 : 0,
                        fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                        color: 'text.primary'
                      }}
                    >
                      {option.title}
                    </Typography>
                    {option.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          opacity: 0.75,
                          whiteSpace: 'pre-line',
                          fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
                          lineHeight: 1.4,
                          color: 'text.primary'
                        }}
                      >
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Goal Detail */}
      {currentStep.id === 'goalDetail' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 900, width: '100%', px: 2 }}>
          {goalDetailMode === 'area' && (
            <>
              <Typography
                variant="h3"
                sx={{
                  mb: 6,
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: { xs: '1.5rem', sm: '2.1rem', md: '2.6rem' },
                  color: 'text.primary',
                  animation: 'fadeInDown 0.6s ease forwards'
                }}
              >
                {t('onboarding.goalDetail.area.title')}
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2.5,
                width: '100%',
                opacity: 0,
                animation: 'fadeInUp 0.5s ease 0.2s forwards'
              }}>
                {[
                  {
                    id: 'finances',
                    title: t('onboarding.goalDetail.area.finances.title'),
                    description: t('onboarding.goalDetail.area.finances.description')
                  },
                  {
                    id: 'health',
                    title: t('onboarding.goalDetail.area.health.title'),
                    description: t('onboarding.goalDetail.area.health.description')
                  },
                  {
                    id: 'movement',
                    title: t('onboarding.goalDetail.area.movement.title'),
                    description: t('onboarding.goalDetail.area.movement.description')
                  },
                  {
                    id: 'work',
                    title: t('onboarding.goalDetail.area.work.title'),
                    description: t('onboarding.goalDetail.area.work.description')
                  },
                  {
                    id: 'organization',
                    title: t('onboarding.goalDetail.area.organization.title'),
                    description: t('onboarding.goalDetail.area.organization.description')
                  }
                ].map((option) => {
                  const isSelected = selectedFocusAreas.includes(option.id);
                  return (
                    <Box
                      key={option.id}
                      onClick={() => {
                        setSelectedFocusAreas((prev) => {
                          if (prev.includes(option.id)) {
                            return prev.filter((id) => id !== option.id);
                          }
                          return [...prev, option.id];
                        });
                      }}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: (theme) =>
                          getPromptCardBackground(theme, isSelected),
                        backdropFilter: (theme) =>
                          theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        WebkitBackdropFilter: (theme) =>
                          theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        borderRadius: 4,
                        border: (theme) => getPromptCardBorder(theme, isSelected),
                        padding: { xs: 2, sm: 2.5, md: 3 },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            getPromptCardHoverBackground(theme, isSelected),
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                            color: 'text.primary'
                          }}
                        >
                          {option.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.75,
                            whiteSpace: 'pre-line',
                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
                            lineHeight: 1.4,
                            color: 'text.primary'
                          }}
                        >
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {goalDetailMode === 'exploration' && (
            <>
              <Typography
                variant="h3"
                sx={{
                  mb: 6,
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: { xs: '1.6rem', sm: '2.15rem', md: '2.6rem' },
                  color: 'text.primary',
                  animation: 'fadeInDown 0.6s ease forwards'
                }}
              >
                {t('onboarding.goalDetail.exploration.title')}
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2.5,
                width: '100%',
                opacity: 0,
                animation: 'fadeInUp 0.5s ease 0.2s forwards'
              }}>
                {[
                  {
                    id: 'examples',
                    title: t('onboarding.goalDetail.exploration.examples.title'),
                    description: t('onboarding.goalDetail.exploration.examples.description')
                  },
                  {
                    id: 'simple',
                    title: t('onboarding.goalDetail.exploration.simple.title'),
                    description: t('onboarding.goalDetail.exploration.simple.description')
                  },
                  {
                    id: 'understand',
                    title: t('onboarding.goalDetail.exploration.understand.title'),
                    description: t('onboarding.goalDetail.exploration.understand.description')
                  },
                  {
                    id: 'unsure',
                    title: t('onboarding.goalDetail.exploration.unsure.title'),
                    description: t('onboarding.goalDetail.exploration.unsure.description')
                  }
                ].map((option) => {
                  const isSelected = selectedExploration === option.id;
                  return (
                    <Box
                      key={option.id}
                      onClick={() => setSelectedExploration(option.id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: (theme) =>
                          getPromptCardBackground(theme, isSelected),
                        backdropFilter: (theme) =>
                          theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        WebkitBackdropFilter: (theme) =>
                          theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        borderRadius: 3,
                        border: (theme) => getPromptCardBorder(theme, isSelected),
                        padding: { xs: 2, sm: 2.5, md: 3 },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            getPromptCardHoverBackground(theme, isSelected),
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                            color: 'text.primary'
                          }}
                        >
                          {option.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.75,
                            whiteSpace: 'pre-line',
                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
                            lineHeight: 1.4,
                            color: 'text.primary'
                          }}
                        >
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {goalDetailMode === 'other' && (
            <>
              <Typography
                variant="h3"
                sx={{
                  mb: 6,
                  fontWeight: 600,
                  textAlign: 'center',
                  fontSize: { xs: '1.45rem', sm: '2.05rem', md: '2.5rem' },
                  color: 'text.primary',
                  animation: 'fadeInDown 0.6s ease forwards'
                }}
              >
                {t('onboarding.goalDetail.other.title')}
              </Typography>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2.5,
                width: '100%',
                opacity: 0,
                animation: 'fadeInUp 0.5s ease 0.2s forwards'
              }}>
                {[
                  {
                    id: 'transition',
                    title: t('onboarding.goalDetail.other.transition.title'),
                    description: t('onboarding.goalDetail.other.transition.description')
                  },
                  {
                    id: 'overload',
                    title: t('onboarding.goalDetail.other.overload.title'),
                    description: t('onboarding.goalDetail.other.overload.description')
                  },
                  {
                    id: 'direction',
                    title: t('onboarding.goalDetail.other.direction.title'),
                    description: t('onboarding.goalDetail.other.direction.description')
                  },
                  {
                    id: 'complexity',
                    title: t('onboarding.goalDetail.other.complexity.title'),
                    description: t('onboarding.goalDetail.other.complexity.description')
                  },
                  {
                    id: 'self',
                    title: t('onboarding.goalDetail.other.self.title'),
                    description: t('onboarding.goalDetail.other.self.description')
                  }
                ].map((option) => {
                  const isSelected = selectedOther === option.id;
                  return (
                    <Box
                      key={option.id}
                      onClick={() => setSelectedOther(option.id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: (theme) =>
                          getPromptCardBackground(theme, isSelected),
                        backdropFilter: (theme) =>
                          theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        WebkitBackdropFilter: (theme) =>
                          theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                        borderRadius: 3,
                        border: (theme) => getPromptCardBorder(theme, isSelected),
                        padding: { xs: 2, sm: 2.5, md: 3 },
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            getPromptCardHoverBackground(theme, isSelected),
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 0.5,
                            fontSize: { xs: '1rem', sm: '1.05rem', md: '1.1rem' },
                            color: 'text.primary'
                          }}
                        >
                          {option.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            opacity: 0.75,
                            whiteSpace: 'pre-line',
                            fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.875rem' },
                            lineHeight: 1.4,
                            color: 'text.primary'
                          }}
                        >
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              {selectedOther === 'self' && (
                <Box sx={{ width: '100%', mt: 3 }}>
                  <TextField
                    fullWidth
                    value={otherCustomText}
                    onChange={(event) => setOtherCustomText(event.target.value)}
                    placeholder={t('onboarding.goalDetail.other.self.placeholder')}
                    variant="outlined"
                    size="small"
                    inputProps={{ maxLength: 120 }}
                    sx={{
                      '& .MuiOutlinedInput-input': {
                        paddingTop: '14px',
                        paddingBottom: '12px',
                        color: 'text.primary'
                      },
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: (theme) =>
                          theme.palette.actualMode === 'wallpaper'
                            ? 'rgba(255, 255, 255, 0.18)'
                            : theme.palette.colors.ui.base,
                        borderRadius: 3,
                        '& fieldset': {
                          border: 'none',
                          top: 0,
                          '& legend': { display: 'none' }
                        },
                        '&:hover fieldset': {
                          border: 'none'
                        },
                        '&.Mui-focused fieldset': {
                          border: (theme) => `2px solid ${theme.palette.colors.ui.accent}`,
                          top: 0
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      {/* Present LifeOS */}
      {currentStep.id === 'presentLifeOS' && (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: { xs: '100%', md: 1200 },
          width: '100%',
          px: { xs: 2, md: 4 },
          gap: { xs: 3, md: 4 }
        }}>
          {/* Video - Mobile: top 3/4, Desktop: right half */}
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              height: { xs: '60vh', md: '70vh' },
              maxHeight: { xs: 450, md: 550 },
              backgroundColor: (theme) =>
                theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : theme.palette.colors.ui.base,
              borderRadius: 3,
              overflow: 'hidden',
              opacity: 0,
              animation: 'fadeInUp 0.6s ease 0.2s forwards',
              order: { xs: 1, md: 2 }
            }}
          >
            <video
              src="/videos/LifeOS.mp4"
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
                transform: 'scale(1.01)'
              }}
            />
          </Box>

          {/* Text Content - Mobile: bottom, Desktop: left half */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            order: { xs: 2, md: 1 }
          }}>
            <Typography
              variant="h3"
              sx={{
                mb: 1.5,
                fontWeight: 700,
                textAlign: { xs: 'center', md: 'left' },
                fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
                color: 'text.primary',
                animation: 'fadeInDown 0.6s ease forwards'
              }}
            >
              {t('onboarding.presentLifeOS.title')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mb: 4,
                fontWeight: 400,
                textAlign: { xs: 'center', md: 'left' },
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                color: 'text.primary',
                opacity: 0,
                '@keyframes fadeInUpDim': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 0.7, transform: 'translateY(0)' }
                },
                animation: 'fadeInUpDim 0.5s ease 0.15s forwards'
              }}
            >
              {t('onboarding.presentLifeOS.subtitle')}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                lineHeight: 1.5,
                color: 'text.primary',
                textAlign: { xs: 'center', md: 'left' },
                opacity: 0,
                animation: 'fadeInUp 0.5s ease 0.25s forwards'
              }}
            >
              {t('onboarding.presentLifeOS.description')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Present AGT */}
      {currentStep.id === 'presentAGT' && (
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: { xs: '100%', md: 1200 },
          width: '100%',
          px: { xs: 2, md: 4 },
          gap: { xs: 3, md: 4 }
        }}>
          {/* Video - Mobile: top 3/4, Desktop: right half */}
          <Box
            sx={{
              width: { xs: '100%', md: '50%' },
              aspectRatio: '1200/1080',
              borderRadius: 3,
              overflow: 'hidden',
              opacity: 0,
              animation: 'fadeInUp 0.6s ease 0.2s forwards',
              order: { xs: 1, md: 2 },
              boxShadow: 'none',
              border: 'none'
            }}
          >
            <video
              src="/videos/AGT.mp4"
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            />
          </Box>

          {/* Text Content - Mobile: bottom, Desktop: left half */}
          <Box sx={{
            width: { xs: '100%', md: '50%' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
            order: { xs: 2, md: 1 }
          }}>
            <Typography
              variant="h3"
              sx={{
                mb: 1.5,
                fontWeight: 700,
                textAlign: { xs: 'center', md: 'left' },
                fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.2rem' },
                color: 'text.primary',
                animation: 'fadeInDown 0.6s ease forwards'
              }}
            >
              {t('onboarding.presentAGT.title')}
            </Typography>

            <Typography
              variant="h6"
              sx={{
                mb: 4,
                fontWeight: 400,
                textAlign: { xs: 'center', md: 'left' },
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                color: 'text.primary',
                opacity: 0,
                '@keyframes fadeInUpDim': {
                  '0%': { opacity: 0, transform: 'translateY(20px)' },
                  '100%': { opacity: 0.7, transform: 'translateY(0)' }
                },
                animation: 'fadeInUpDim 0.5s ease 0.15s forwards'
              }}
            >
              {t('onboarding.presentAGT.subtitle')}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                lineHeight: 1.5,
                color: 'text.primary',
                textAlign: { xs: 'center', md: 'left' },
                opacity: 0,
                animation: 'fadeInUp 0.5s ease 0.25s forwards'
              }}
            >
              {t('onboarding.presentAGT.description')}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Continue/Finish Button */}
      <Box
        sx={{
          position: 'absolute',
          bottom: { xs: 80, sm: 100, md: 120 },
          display: 'flex',
          gap: 2
        }}
      >
        {/* Back button - only show on AGT to go back to LifeOS */}
        {currentStep.id === 'presentAGT' && (
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={isSubmitting}
            sx={{
              textTransform: 'none',
              boxShadow: 'none',
              borderRadius: 999,
              paddingX: 4,
              paddingY: 1.5,
              backgroundColor: 'transparent',
              color: (theme) =>
                theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.colors.ui.accent,
              border: (theme) =>
                theme.palette.actualMode === 'wallpaper'
                  ? '1px solid rgba(255,255,255,0.3)'
                  : `1px solid ${theme.palette.colors.ui.accent}`,
              '&:hover': {
                backgroundColor: (theme) =>
                  theme.palette.actualMode === 'wallpaper' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
                boxShadow: 'none'
              }
            }}
          >
            {t('onboarding.back')}
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleNext}
          disabled={
            isSubmitting ||
            (currentStep.id === 'goal' && selectedGoals.length === 0) ||
            (currentStep.id === 'goalDetail' && !isGoalDetailComplete)
          }
          sx={{
            textTransform: 'none',
            boxShadow: 'none',
            borderRadius: 999,
            paddingX: 4,
            paddingY: 1.5,
            backgroundColor: (theme) =>
              theme.palette.actualMode === 'wallpaper' ? 'rgba(255,255,255,0.18)' : theme.palette.colors.ui.accent,
            color: (theme) =>
              theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.colors.system.white,
            border: (theme) =>
              theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(255,255,255,0.3)' : '1px solid transparent',
            '&:hover': {
              backgroundColor: (theme) =>
                theme.palette.actualMode === 'wallpaper' ? 'rgba(255,255,255,0.26)' : theme.palette.colors.ui.accent,
              opacity: theme => (theme.palette.actualMode === 'wallpaper' ? 1 : 0.9),
              boxShadow: 'none'
            },
            '&.Mui-disabled': {
              backgroundColor: (theme) =>
                theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255,255,255,0.08)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
              color: (theme) =>
                theme.palette.actualMode === 'wallpaper'
                  ? 'rgba(255,255,255,0.35)'
                  : (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.3)'),
              border: (theme) =>
                theme.palette.actualMode === 'wallpaper'
                  ? '1px solid rgba(255,255,255,0.1)'
                  : '1px solid transparent'
            }
          }}
        >
          {isLastStep ? t('onboarding.finish') : t('onboarding.continue')}
        </Button>
      </Box>
    </Box>
  );
};

export default AppOnboarding;
